import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router'
import { slugifyAlias } from '@threadline/shared'
import { readActiveAccountHandle, setActiveAccountHandle } from './api/activeAccount.ts'
import {
  findMostRecentlyUsedAccount,
  findSavedAccountById,
  reloadAccountBook,
} from './composables/useAccountBook.ts'
import {
  loadAuthenticationStatus,
  resetAuthenticationStatus,
  useAuthenticationSession,
} from './composables/useAuthenticationSession.ts'

/**
 * Every authenticated screen lives under the code of the account viewing it, which is what
 * lets two accounts be open in two tabs at once: a tab reads which account it is acting as
 * from its own URL rather than from state the two tabs would share.
 *
 * Beside each code is a name, and **nothing reads the names**. They are there so a person
 * can tell one tab from another and recognize a link before opening it; the codes carry the
 * resolution. That is what lets a name be changed at any moment without breaking a link
 * that already exists, and what makes two people sharing a name harmless.
 */
const applicationRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'application-root',
    component: () => import('./views/NotFoundView.vue'),
    meta: { requiresAuthentication: false },
  },
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('./views/SignInView.vue'),
    meta: { requiresAuthentication: false },
  },
  {
    // Where a browser navigation login lands, carrying the new session token in the URL
    // fragment. It belongs to no account yet, so it is deliberately unprefixed.
    path: '/sign-in/complete',
    name: 'sign-in-complete',
    component: () => import('./views/SignInCompletionView.vue'),
    meta: { requiresAuthentication: false },
  },
  {
    // A message link is shared with other people, so it never carries the account of
    // whoever copied it. It is resolved into the account this browser is using.
    path: '/messages/:messageId',
    name: 'message-deep-link-entry',
    component: () => import('./views/NotFoundView.vue'),
    meta: { requiresAuthentication: false },
  },
  {
    path: '/:accountId/:accountAlias',
    name: 'conversation-list',
    component: () => import('./views/ChatsView.vue'),
    meta: { requiresAuthentication: true },
  },
  {
    // `settings` and `messages` sit where a counterpart code would go, and cannot be
    // mistaken for one: a generated code always carries a hyphen, and both words are
    // reserved so a chosen name can never take them either.
    path: '/:accountId/:accountAlias/settings',
    name: 'settings',
    component: () => import('./views/SettingsView.vue'),
    meta: { requiresAuthentication: true },
  },
  {
    // The administration: a routing word like `settings`, reserved for the same reason,
    // with its sections below it. Declared before the conversation shape for the same
    // reason `settings` is.
    path: '/:accountId/:accountAlias/operator',
    component: () => import('./views/AdminLayout.vue'),
    meta: { requiresAuthentication: true },
    children: [
      {
        path: '',
        name: 'operator',
        component: () => import('./views/AdminDeliveriesView.vue'),
      },
      {
        path: 'deliveries',
        name: 'operator-deliveries',
        component: () => import('./views/AdminDeliveriesView.vue'),
        meta: { requiresAuthentication: true },
      },
      {
        path: 'accounts',
        name: 'operator-accounts',
        component: () => import('./views/AdminAccountsView.vue'),
        meta: { requiresAuthentication: true },
      },
      {
        path: 'messages',
        name: 'operator-messages',
        component: () => import('./views/AdminMessagesView.vue'),
        meta: { requiresAuthentication: true },
      },
      {
        path: 'notifications',
        name: 'operator-notifications',
        component: () => import('./views/AdminNotificationsView.vue'),
        meta: { requiresAuthentication: true },
      },
      {
        path: 'ban',
        name: 'operator-ban',
        component: () => import('./views/AdminBanView.vue'),
        meta: { requiresAuthentication: true },
      },
    ],
  },
  {
    // The conversations somebody took out of their list.
    //
    // Archived is a **context**, not a screen: the same chat layout, with the other list on
    // the left. Somebody reading an archived conversation is still using the application —
    // writing, opening threads — and only the list beside them is different, which is why
    // all three shapes below render `ChatsView` exactly as the invite word does.
    //
    // The word is spelled out rather than left as a free parameter, for the same reason the
    // invite words are: it sits where a counterpart code would go. All three spellings are
    // accepted whatever language a deployment serves, since `intl` serves several.
    path: '/:accountId/:accountAlias/:archivedSegment(archived|arquivadas|archivadas)',
    name: 'archived-conversations',
    component: () => import('./views/ChatsView.vue'),
    meta: { requiresAuthentication: true },
  },
  {
    // Reading one of them. This has the same four segments as an ordinary conversation and
    // is only told apart by the constrained word, so it must stay declared before it.
    path: '/:accountId/:accountAlias/:archivedSegment(archived|arquivadas|archivadas)/:counterpartId/:counterpartAlias',
    name: 'archived-conversation',
    component: () => import('./views/ChatsView.vue'),
    meta: { requiresAuthentication: true },
  },
  {
    // A thread inside one of them. Without this, opening a thread would drop somebody back
    // into the main list mid-conversation.
    path: '/:accountId/:accountAlias/:archivedSegment(archived|arquivadas|archivadas)/:counterpartId/:counterpartAlias/threads/:rootMessageId',
    name: 'archived-conversation-thread',
    component: () => import('./views/ChatsView.vue'),
    meta: { requiresAuthentication: true },
  },
  {
    path: '/:accountId/:accountAlias/messages/:messageId',
    name: 'message-deep-link',
    component: () => import('./views/MessageDeepLinkView.vue'),
    meta: { requiresAuthentication: true },
  },
  {
    // Writing to somebody who has no account yet: there is no code to name them by until
    // the first message brings one into existence, so the address stands in its place.
    //
    // The word is spelled out rather than left as a free parameter, because this path has
    // the same shape as a conversation and would otherwise swallow every one of them. All
    // three spellings are accepted whatever language a deployment serves, since `intl`
    // serves several.
    path: '/:accountId/:accountAlias/:inviteSegment(invite|convidar|invitar)/:counterpartHandle',
    name: 'invite-conversation',
    component: () => import('./views/ChatsView.vue'),
    meta: { requiresAuthentication: true },
  },
  {
    path: '/:accountId/:accountAlias/:counterpartId/:counterpartAlias',
    name: 'conversation',
    component: () => import('./views/ChatsView.vue'),
    meta: { requiresAuthentication: true },
  },
  {
    path: '/:accountId/:accountAlias/:counterpartId/:counterpartAlias/threads/:rootMessageId',
    name: 'conversation-thread',
    component: () => import('./views/ChatsView.vue'),
    meta: { requiresAuthentication: true },
  },
  {
    path: '/:notFoundPath(.*)*',
    name: 'not-found',
    component: () => import('./views/NotFoundView.vue'),
    meta: { requiresAuthentication: false },
  },
]

export const applicationRouter = createRouter({
  history: createWebHistory(),
  routes: applicationRoutes,
  scrollBehavior: () => ({ top: 0 }),
})

function readAccountIdParameter(route: RouteLocationNormalized): string | null {
  const accountIdParameter = route.params['accountId']
  return typeof accountIdParameter === 'string' && accountIdParameter.length > 0
    ? accountIdParameter
    : null
}

/**
 * The part of a destination below the account, which survives the sign-in round trip.
 *
 * Both leading segments go: the code and the name beside it belong to whichever account
 * ends up signing in, and that may not be the one the link named.
 */
function readPathBelowAccount(route: RouteLocationNormalized, accountId: string): string {
  const accountPrefix = `/${accountId}`
  if (!route.fullPath.startsWith(accountPrefix)) {
    return route.fullPath
  }
  const pathBelowCode = route.fullPath.slice(accountPrefix.length)
  // Drop the name segment that follows the code, which belongs to the account as well.
  return pathBelowCode.replace(/^\/[^/]*/, '')
}

/**
 * Resolves the account a navigation belongs to and, when it is one this browser knows,
 * makes it the account this tab acts as before anything is loaded for it.
 *
 * A deep link preserves its destination through the whole authentication round trip, and
 * an account whose token the server no longer accepts keeps its place on the sign-in
 * screen: it is offered again, preselected, rather than quietly disappearing.
 */
applicationRouter.beforeEach(async (destinationRoute) => {
  reloadAccountBook()

  if (destinationRoute.name === 'message-deep-link-entry') {
    const messageId = destinationRoute.params['messageId']
    const mostRecentAccount = findMostRecentlyUsedAccount()
    return mostRecentAccount === null
      ? { name: 'sign-in', query: { redirectPath: destinationRoute.fullPath } }
      : {
          name: 'message-deep-link',
          params: {
            accountId: mostRecentAccount.accountId,
            accountAlias: slugifyAlias(mostRecentAccount.alias ?? mostRecentAccount.handle),
            messageId,
          },
        }
  }

  if (destinationRoute.name === 'application-root') {
    const mostRecentAccount = findMostRecentlyUsedAccount()
    return mostRecentAccount === null
      ? { name: 'sign-in' }
      : {
          name: 'conversation-list',
          params: {
            accountId: mostRecentAccount.accountId,
            accountAlias: slugifyAlias(mostRecentAccount.alias ?? mostRecentAccount.handle),
          },
        }
  }

  const { isAuthenticated, authenticationStatusLoaded } = useAuthenticationSession()

  if (destinationRoute.meta['requiresAuthentication'] !== true) {
    // The sign-in screen needs to know what this deployment offers — which ways of signing
    // in exist, and in which language — before it can render itself.
    if (!authenticationStatusLoaded.value) {
      await loadAuthenticationStatus()
    }
    return true
  }

  const accountId = readAccountIdParameter(destinationRoute)
  const savedAccount = accountId === null ? null : findSavedAccountById(accountId)
  if (accountId === null || savedAccount === null) {
    // The destination is preserved either way, so signing in lands on the screen that was
    // asked for rather than on a generic starting point.
    return {
      name: 'sign-in',
      query: {
        accountHandle: savedAccount?.handle ?? undefined,
        redirectPath:
          accountId === null
            ? destinationRoute.fullPath
            : readPathBelowAccount(destinationRoute, accountId),
      },
    }
  }

  if (readActiveAccountHandle() !== savedAccount.handle) {
    setActiveAccountHandle(savedAccount.handle)
    resetAuthenticationStatus()
  }

  if (!authenticationStatusLoaded.value) {
    await loadAuthenticationStatus()
  }

  if (!isAuthenticated.value) {
    // The token this browser holds is no longer accepted. The account stays listed so the
    // person can sign in again with one click instead of retyping their address.
    return {
      name: 'sign-in',
      query: {
        accountHandle: savedAccount.handle,
        rejectedAccountId: savedAccount.accountId,
        redirectPath: readPathBelowAccount(destinationRoute, accountId),
      },
    }
  }

  return true
})
